import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  Divider,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
  TablePagination
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Schedule as ScheduleIcon,
  Room as RoomIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  SwapHoriz as SwapHorizIcon,
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  Print as PrintIcon,
  Category as CategoryIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { relatoriosApi } from '../../services/relatoriosApi';

const ModalSenhasUsuario = ({ 
  open, 
  onClose, 
  usuario, 
  filtros = {},
  customTheme,
  usuarios = [], // Lista de todos os usuários para navegação
  onUsuarioChange = null // Callback para mudança de usuário
}) => {
  const theme = useTheme();
  const [dadosUsuario, setDadosUsuario] = useState(null);
  const [senhas, setSenhas] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rankingEficiencia, setRankingEficiencia] = useState(null);
  const [rankingVolume, setRankingVolume] = useState(null);

  // Funções de navegação entre usuários
  const usuarioAtualIndex = usuarios.findIndex(u => u.id === usuario?.id);
  const podeNavegar = usuarios.length > 1;
  const temUsuarioAnterior = usuarioAtualIndex > 0;
  const temProximoUsuario = usuarioAtualIndex < usuarios.length - 1;

  const navegarParaUsuarioAnterior = () => {
    if (temUsuarioAnterior && onUsuarioChange) {
      const usuarioAnterior = usuarios[usuarioAtualIndex - 1];
      onUsuarioChange(usuarioAnterior);
    }
  };

  const navegarParaProximoUsuario = () => {
    if (temProximoUsuario && onUsuarioChange) {
      const proximoUsuario = usuarios[usuarioAtualIndex + 1];
      onUsuarioChange(proximoUsuario);
    }
  };

  // Carregar dados do usuário
  const carregarDadosUsuario = async () => {
    if (!usuario?.id) return;

    setCarregando(true);
    setErro(null);

    try {
      // Buscar dados específicos do usuário
      const response = await relatoriosApi.buscarSenhasAtendidasPorUsuario(usuario.id, filtros);
      
      if (response.success) {
        setDadosUsuario(response.data.usuario);
        setSenhas(response.data.senhas || []);
      } else {
        setErro(response.message || 'Erro ao carregar dados do usuário');
      }

      // Buscar dados de todos os usuários para calcular rankings
      await carregarRankings();
    } catch (error) {
      console.error('Erro ao carregar dados do usuário:', error);
      setErro('Erro de conexão com o servidor');
    } finally {
      setCarregando(false);
    }
  };

  // Carregar rankings de todos os usuários
  const carregarRankings = async () => {
    try {
      console.log('[DEBUG] Carregando rankings com filtros:', filtros);
      const response = await relatoriosApi.buscarRelatorioUsuariosDetalhado(filtros);
      console.log('[DEBUG] Resposta do ranking:', response);
      
      if (response.success && response.data && Array.isArray(response.data)) {
        const usuarios = response.data;
        console.log('[DEBUG] Usuários recebidos:', usuarios);
        
        // Ranking por eficiência (tempo médio - menor é melhor)
        // Usar campo 'tempoMedioMs' se existir, senão tentar converter 'tmd' ou usar 'tme'
        const rankingTempo = [...usuarios]
          .filter(u => {
            // Aceitar usuários que têm atendimentos
            const temAtendimentos = (u.atendimentos || u.totalAtendimentos || 0) > 0;
            console.log('[DEBUG] Usuário para ranking tempo:', { 
              id: u.id, 
              nome: u.nome, 
              atendimentos: u.atendimentos,
              totalAtendimentos: u.totalAtendimentos,
              tempoMedioMs: u.tempoMedioMs,
              tmd: u.tmd,
              tme: u.tme,
              temAtendimentos
            });
            return temAtendimentos;
          })
          .sort((a, b) => {
            // Tentar usar tempoMedioMs primeiro, senão usar uma estimativa baseada em tmd/tme
            const tempoA = a.tempoMedioMs || estimarTempoMs(a.tmd || a.tme) || 999999;
            const tempoB = b.tempoMedioMs || estimarTempoMs(b.tmd || b.tme) || 999999;
            return tempoA - tempoB;
          })
          .map((u, index) => ({ id: u.id, posicao: index + 1 }));
        
        console.log('[DEBUG] Ranking por tempo:', rankingTempo);
        
        // Ranking por volume (quantidade - maior é melhor)
        const rankingQuantidade = [...usuarios]
          .filter(u => {
            const atendimentos = u.atendimentos || u.totalAtendimentos || 0;
            console.log('[DEBUG] Usuário para ranking volume:', { 
              id: u.id, 
              nome: u.nome, 
              atendimentos: u.atendimentos,
              totalAtendimentos: u.totalAtendimentos
            });
            return atendimentos > 0;
          })
          .sort((a, b) => {
            const atendimentosA = a.atendimentos || a.totalAtendimentos || 0;
            const atendimentosB = b.atendimentos || b.totalAtendimentos || 0;
            return atendimentosB - atendimentosA;
          })
          .map((u, index) => ({ id: u.id, posicao: index + 1 }));

        console.log('[DEBUG] Ranking por volume:', rankingQuantidade);

        // Encontrar posições do usuário atual
        const posicaoEficiencia = rankingTempo.find(r => r.id === usuario?.id)?.posicao || '--';
        const posicaoVolume = rankingQuantidade.find(r => r.id === usuario?.id)?.posicao || '--';

        console.log('[DEBUG] Posições encontradas:', { 
          usuarioId: usuario?.id, 
          posicaoEficiencia, 
          posicaoVolume 
        });

        setRankingEficiencia(posicaoEficiencia);
        setRankingVolume(posicaoVolume);
      } else {
        console.warn('[DEBUG] Resposta inválida para rankings:', response);
        setRankingEficiencia('--');
        setRankingVolume('--');
      }
    } catch (error) {
      console.error('[DEBUG] Erro ao carregar rankings:', error);
      setRankingEficiencia('--');
      setRankingVolume('--');
    }
  };

  // Carregar dados quando o modal abrir
  useEffect(() => {
    if (open && usuario?.id) {
      carregarDadosUsuario();
    }
  }, [open, usuario?.id, filtros]);

  // Limpar dados quando fechar
  useEffect(() => {
    if (!open) {
      setDadosUsuario(null);
      setSenhas([]);
      setErro(null);
      setPage(0);
      setRankingEficiencia(null);
      setRankingVolume(null);
    }
  }, [open]);

  // Suporte para navegação via teclado
  useEffect(() => {
    if (!open || !podeNavegar) return;

    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft' && temUsuarioAnterior) {
        event.preventDefault();
        navegarParaUsuarioAnterior();
      } else if (event.key === 'ArrowRight' && temProximoUsuario) {
        event.preventDefault();
        navegarParaProximoUsuario();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, podeNavegar, temUsuarioAnterior, temProximoUsuario, navegarParaUsuarioAnterior, navegarParaProximoUsuario]);

  // Função para formatar data
  const formatarData = (data) => {
    if (!data) return '--';
    try {
      return format(new Date(data), 'dd/MM/yyyy HH:mm', { locale: pt });
    } catch (error) {
      return '--';
    }
  };

  // Função para obter cor do status
  const getStatusColor = (status) => {
    switch (status) {
      case 'FINALIZADA':
        return '#4caf50';
      case 'CANCELADA':
        return '#f44336';
      case 'EM_ATENDIMENTO':
        return '#ff9800';
      default:
        return '#9e9e9e';
    }
  };

  // Função para obter ícone do status
  const getStatusIcon = (status) => {
    switch (status) {
      case 'FINALIZADA':
        return <CheckCircleIcon sx={{ fontSize: 16 }} />;
      case 'CANCELADA':
        return <CancelIcon sx={{ fontSize: 16 }} />;
      default:
        return <ScheduleIcon sx={{ fontSize: 16 }} />;
    }
  };

  // Função para traduzir status
  const traduzirStatus = (status) => {
    switch (status) {
      case 'FINALIZADA':
        return 'Finalizada';
      case 'CANCELADA':
        return 'Cancelada';
      case 'EM_ATENDIMENTO':
        return 'Em Atendimento';
      default:
        return status;
    }
  };

  // Paginação
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const senhasPaginadas = senhas.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Função para estimar tempo em ms a partir de string como "10h 18min 31seg"
  const estimarTempoMs = (tempoStr) => {
    if (!tempoStr || typeof tempoStr !== 'string') return 0;
    
    let totalMs = 0;
    
    // Extrair horas
    const horasMatch = tempoStr.match(/(\d+)h/);
    if (horasMatch) {
      totalMs += parseInt(horasMatch[1]) * 60 * 60 * 1000;
    }
    
    // Extrair minutos
    const minutosMatch = tempoStr.match(/(\d+)min/);
    if (minutosMatch) {
      totalMs += parseInt(minutosMatch[1]) * 60 * 1000;
    }
    
    // Extrair segundos
    const segundosMatch = tempoStr.match(/(\d+)seg/);
    if (segundosMatch) {
      totalMs += parseInt(segundosMatch[1]) * 1000;
    }
    
    return totalMs;
  };

  // Função para calcular tempo de atendimento
  const calcularTempoAtendimento = (senha) => {
    if (!senha.iniciada_em || !senha.finalizada_em) {
      return '--';
    }
    
    try {
      const inicio = new Date(senha.iniciada_em);
      const fim = new Date(senha.finalizada_em);
      const diferencaMs = fim - inicio;
      
      if (diferencaMs <= 0) return '--';
      
      const horas = Math.floor(diferencaMs / (1000 * 60 * 60));
      const minutos = Math.floor((diferencaMs % (1000 * 60 * 60)) / (1000 * 60));
      const segundos = Math.floor((diferencaMs % (1000 * 60)) / 1000);
      
      if (horas > 0) {
        return `${horas}h ${minutos}min ${segundos}seg`;
      } else if (minutos > 0) {
        return `${minutos}min ${segundos}seg`;
      } else {
        return `${segundos}seg`;
      }
    } catch (error) {
      return '--';
    }
  };

  // Função para imprimir o relatório
  const handleImprimir = () => {
    if (!dadosUsuario || senhas.length === 0) {
      return;
    }

    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Relatório Detalhado - ${dadosUsuario.nome}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              line-height: 1.6;
              color: #333;
              background: white;
              padding: 20px;
            }
            
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 3px solid #1976d2;
              padding-bottom: 20px;
            }
            
            .header h1 {
              color: #1976d2;
              font-size: 28px;
              margin-bottom: 10px;
            }
            
            .header p {
              color: #666;
              font-size: 14px;
            }
            
            .user-info {
              background: #f8f9fa;
              border-radius: 8px;
              padding: 20px;
              margin-bottom: 30px;
              border-left: 4px solid #1976d2;
            }
            
            .user-profile {
              display: flex;
              align-items: center;
              margin-bottom: 20px;
            }
            
            .user-avatar {
              width: 60px;
              height: 60px;
              background: #1976d2;
              color: white;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 24px;
              font-weight: bold;
              margin-right: 20px;
            }
            
            .user-details h2 {
              color: #1976d2;
              font-size: 24px;
              margin-bottom: 5px;
            }
            
            .user-details p {
              color: #666;
              font-size: 14px;
            }
            
            .metrics {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr;
              gap: 20px;
              margin-top: 20px;
            }
            
            .metric-card {
              background: white;
              border: 1px solid #e0e0e0;
              border-radius: 8px;
              padding: 20px;
              text-align: center;
            }
            
            .metric-card h3 {
              font-size: 32px;
              color: #1976d2;
              margin-bottom: 5px;
            }
            
            .metric-card p {
              color: #666;
              font-size: 14px;
            }
            
            .senhas-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 30px;
              font-size: 12px;
            }
            
            .senhas-table th,
            .senhas-table td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            
            .senhas-table th {
              background: #1976d2;
              color: white;
              font-weight: bold;
            }
            
            .senhas-table tr:nth-child(even) {
              background: #f8f9fa;
            }
            
            .status-chip {
              padding: 4px 8px;
              border-radius: 12px;
              font-size: 10px;
              font-weight: bold;
              color: white;
            }
            
            .status-finalizada {
              background: #4caf50;
            }
            
            .status-cancelada {
              background: #f44336;
            }
            
            .status-em-atendimento {
              background: #ff9800;
            }
            
                         .preferencial {
               background: #ffebee;
               color: #f44336;
               font-weight: bold;
               padding: 4px 8px;
               border-radius: 12px;
               border: 1px solid #ffcdd2;
               font-size: 11px;
             }
             
             .comum {
               background: #e3f2fd;
               color: #2196f3;
               font-weight: bold;
               padding: 4px 8px;
               border-radius: 12px;
               border: 1px solid #bbdefb;
               font-size: 11px;
             }
            
            .servicos-section {
              background: #f8f9fa;
              border-radius: 8px;
              padding: 20px;
              margin-bottom: 30px;
              border-left: 4px solid #9c27b0;
            }
            
            .servicos-section h3 {
              color: #9c27b0;
              font-size: 20px;
              margin-bottom: 15px;
              display: flex;
              align-items: center;
            }
            
            .servicos-grid {
              display: grid;
              grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
              gap: 12px;
              margin-top: 15px;
            }
            
            .servico-card {
              background: white;
              border: 1px solid #e0e0e0;
              border-radius: 6px;
              padding: 12px;
              position: relative;
              border-left: 3px solid #9c27b0;
            }
            
            .servico-card h4 {
              color: #333;
              font-size: 13px;
              margin-bottom: 6px;
              line-height: 1.3;
              max-height: 2.6em;
              overflow: hidden;
              text-overflow: ellipsis;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
            }
            
            .servico-stats {
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            
            .servico-quantidade {
              background: #9c27b0;
              color: white;
              padding: 2px 8px;
              border-radius: 12px;
              font-size: 11px;
              font-weight: bold;
            }
            
            .servico-percentual {
              color: #666;
              font-size: 11px;
            }
            
            .footer {
              margin-top: 30px;
              text-align: center;
              color: #666;
              font-size: 12px;
              border-top: 1px solid #ddd;
              padding-top: 20px;
            }
            
            @media print {
              body {
                padding: 0;
              }
              
              .metrics {
                grid-template-columns: 1fr 1fr 1fr;
                gap: 15px;
              }
              
              .metric-card {
                padding: 15px;
              }
              
              .metric-card h3 {
                font-size: 28px;
              }
              
              .senhas-table {
                font-size: 10px;
              }
              
              .senhas-table th,
              .senhas-table td {
                padding: 6px;
              }
              
              .servicos-grid {
                grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
                gap: 10px;
              }
              
              .servico-card {
                padding: 10px;
              }
            }
            
            @media print and (max-width: 800px) {
              .metrics {
                grid-template-columns: 1fr 1fr;
              }
              
              .servicos-grid {
                grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Relatório Detalhado de Atendimentos</h1>
            <p>Gerado em ${format(new Date(), 'dd/MM/yyyy HH:mm')}</p>
          </div>
          
          <div class="user-info">
            <div class="user-profile">
              <div class="user-avatar">
                ${dadosUsuario.nome.charAt(0).toUpperCase()}
              </div>
              <div class="user-details">
                <h2>${dadosUsuario.nome}</h2>
                <p>Atendente • Período: ${filtros.dataInicio ? format(new Date(filtros.dataInicio), 'dd/MM/yyyy') : 'N/A'} até ${filtros.dataFim ? format(new Date(filtros.dataFim), 'dd/MM/yyyy') : 'N/A'}</p>
              </div>
            </div>
            
            <div class="metrics">
              <div class="metric-card">
                <h3>${dadosUsuario.totalAtendimentos}</h3>
                <p>Total de Atendimentos</p>
              </div>
              <div class="metric-card">
                <h3>${dadosUsuario.tempoMedioAtendimento}</h3>
                <p>Tempo Médio por Atendimento</p>
              </div>
              <div class="metric-card">
                <h3>${dadosUsuario.totalServicosDistintos || 0}</h3>
                <p>Tipos de Serviços Atendidos</p>
              </div>
            </div>
          </div>
          
          ${dadosUsuario.servicosAtendidos && dadosUsuario.servicosAtendidos.length > 0 ? `
          <div class="servicos-section">
            <h3>📊 Serviços Atendidos (${dadosUsuario.totalServicosDistintos || 0} tipos diferentes)</h3>
            <p style="color: #666; font-size: 14px; margin-bottom: 10px;">
              Distribuição por tipo de serviço no período selecionado
            </p>
            <div class="servicos-grid">
              ${dadosUsuario.servicosAtendidos.map(servico => `
                <div class="servico-card">
                  <h4>${servico.nome}</h4>
                  <div class="servico-stats">
                    <span class="servico-quantidade">${servico.quantidade}</span>
                    <span class="servico-percentual">${((servico.quantidade / dadosUsuario.totalAtendimentos) * 100).toFixed(1)}%</span>
                  </div>
                </div>
              `).join('')}
            </div>
          </div>
          ` : ''}
          
                    <table class="senhas-table">
            <thead>
              <tr>
                <th>Senha</th>
                <th>Serviço</th>
                <th>Tipo</th>
                <th>Status</th>
                <th>Guichê</th>
                <th>Emissão</th>
                <th>Chamada</th>
                <th>Início</th>
                <th>Finalização</th>
                <th>Tempo Atendimento</th>
                <th>Serviços Adicionais</th>
              </tr>
            </thead>
            <tbody>
              ${senhas.map(senha => `
                 <tr>
                   <td>
                     ${senha.senha}
                     ${senha.origem === 'historico' ? ' (H)' : ''}
                   </td>
                   <td>
                     ${senha.servico_nome || '--'}
                     ${senha.servico_alterado ? ' (Alterado de: ' + (senha.servico_original || '') + ')' : ''}
                   </td>
                   <td>
                     <span class="${senha.preferencial ? 'preferencial' : 'comum'}">
                       ${senha.preferencial ? 'Preferencial' : 'Comum'}
                     </span>
                   </td>
                   <td>
                     <span class="status-chip status-${senha.status?.toLowerCase().replace('_', '-') || 'default'}">
                       ${traduzirStatus(senha.status)}
                     </span>
                   </td>
                   <td>${senha.guiche || '--'}</td>
                   <td>${formatarData(senha.emitida_em)}</td>
                   <td>${formatarData(senha.chamada_em)}</td>
                   <td>${formatarData(senha.iniciada_em)}</td>
                   <td>${formatarData(senha.finalizada_em)}</td>
                   <td style="font-family: monospace; font-weight: bold; color: ${calcularTempoAtendimento(senha) !== '--' ? '#4caf50' : '#666'};">
                     ${calcularTempoAtendimento(senha)}
                   </td>
                   <td>
                     ${senha.servicos_adicionais && Array.isArray(senha.servicos_adicionais) && senha.servicos_adicionais.length > 0 
                       ? senha.servicos_adicionais.join(', ') 
                       : '--'
                     }
                   </td>
                 </tr>
               `).join('')}
            </tbody>
          </table>
          
          <div class="footer">
            <p>Sistema de Gerenciamento de Senhas - Relatório gerado automaticamente</p>
            <p>Total de registros: ${senhas.length}</p>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Aguarda o carregamento e imprime
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          width: '98vw',
          maxWidth: '1800px',
          height: '92vh',
          maxHeight: '92vh'
        }
      }}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          maxHeight: '90vh',
          bgcolor: theme.palette.background.paper,
          animation: 'modalSlideIn 0.4s ease-out',
          '@keyframes modalSlideIn': {
            '0%': {
              opacity: 0,
              transform: 'scale(0.9) translateY(-20px)'
            },
            '100%': {
              opacity: 1,
              transform: 'scale(1) translateY(0)'
            }
          },
          '@keyframes pulse': {
            '0%, 100%': {
              opacity: 1,
              transform: 'scale(1)'
            },
            '50%': {
              opacity: 0.7,
              transform: 'scale(1.1)'
            }
          }
        }
      }}
    >
      <DialogTitle
        sx={{
          pb: 0,
          pt: 3,
          px: 0,
          position: 'relative',
          background: `linear-gradient(135deg, ${alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.08)} 0%, ${alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.02)} 100%)`,
          borderBottom: 'none',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '60px',
            height: '3px',
            background: `linear-gradient(90deg, ${customTheme?.palette?.primary?.main || theme.palette.primary.main}, ${alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.6)})`,
            borderRadius: '2px'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pb: 3, position: 'relative' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, width: '33.3%' }}>
            
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '16px',
                background: `linear-gradient(135deg, ${customTheme?.palette?.primary?.main || theme.palette.primary.main}, ${alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.8)})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 8px 24px ${alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.25)}`,
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: '-2px',
                  borderRadius: '18px',
                  background: `linear-gradient(135deg, ${customTheme?.palette?.primary?.main || theme.palette.primary.main}, ${alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.6)})`,
                  zIndex: -1,
                  opacity: 0.3
                }
              }}
            >
              <AssignmentIcon sx={{ fontSize: 28, color: 'white' }} />
            </Box>
            <Box>
              <Typography 
                variant="h4" 
                fontWeight="bold"
                sx={{
                  background: `linear-gradient(45deg, ${customTheme?.palette?.primary?.main || theme.palette.primary.main}, ${alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.7)})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 0.5
                }}
              >
                Relatório Detalhado
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                Análise completa de atendimentos realizados
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '33.3%' }}>
            {/* Setas de Navegação */}
            {podeNavegar && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title={temUsuarioAnterior ? `Anterior: ${usuarios[usuarioAtualIndex - 1]?.nome} (←)` : "Primeiro usuário"}>
                  <span>
                    <IconButton
                      onClick={navegarParaUsuarioAnterior}
                      disabled={!temUsuarioAnterior}
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.1),
                        color: customTheme?.palette?.primary?.main || theme.palette.primary.main,
                        border: `1px solid ${alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.2)}`,
                        '&:hover': {
                          bgcolor: alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.15),
                          transform: 'scale(1.05)',
                          boxShadow: `0 4px 12px ${alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.2)}`
                        },
                        '&:disabled': {
                          bgcolor: alpha(theme.palette.action.disabled, 0.1),
                          color: theme.palette.action.disabled,
                          border: `1px solid ${alpha(theme.palette.action.disabled, 0.2)}`,
                          transform: 'none',
                          boxShadow: 'none'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <ChevronLeftIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                
                <Box sx={{ textAlign: 'center', mx: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {usuarioAtualIndex + 1} de {usuarios.length}
                  </Typography>
                </Box>
                
                <Tooltip title={temProximoUsuario ? `Próximo: ${usuarios[usuarioAtualIndex + 1]?.nome} (→)` : "Último usuário"}>
                  <span>
                    <IconButton
                      onClick={navegarParaProximoUsuario}
                      disabled={!temProximoUsuario}
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.1),
                        color: customTheme?.palette?.primary?.main || theme.palette.primary.main,
                        border: `1px solid ${alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.2)}`,
                        '&:hover': {
                          bgcolor: alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.15),
                          transform: 'scale(1.05)',
                          boxShadow: `0 4px 12px ${alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.2)}`
                        },
                        '&:disabled': {
                          bgcolor: alpha(theme.palette.action.disabled, 0.1),
                          color: theme.palette.action.disabled,
                          border: `1px solid ${alpha(theme.palette.action.disabled, 0.2)}`,
                          transform: 'none',
                          boxShadow: 'none'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <ChevronRightIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, width: '33.3%', justifyContent: 'flex-end' }}>
            <Tooltip title={!dadosUsuario || senhas.length === 0 ? "Nenhum dado para imprimir" : "Imprimir relatório completo"}>
              <span>
                <IconButton
                  onClick={handleImprimir}
                  disabled={!dadosUsuario || senhas.length === 0}
                  sx={{
                    width: 44,
                    height: 44,
                    bgcolor: alpha('#4caf50', 0.1),
                    color: '#4caf50',
                    border: `1px solid ${alpha('#4caf50', 0.2)}`,
                    '&:hover': {
                      bgcolor: alpha('#4caf50', 0.15),
                      transform: 'scale(1.05)',
                      boxShadow: `0 4px 12px ${alpha('#4caf50', 0.2)}`
                    },
                    '&:disabled': {
                      bgcolor: alpha(theme.palette.action.disabled, 0.1),
                      color: theme.palette.action.disabled,
                      border: `1px solid ${alpha(theme.palette.action.disabled, 0.2)}`,
                      transform: 'none',
                      boxShadow: 'none'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <PrintIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
            
            <IconButton
              onClick={onClose}
              sx={{
                width: 44,
                height: 44,
                bgcolor: alpha(theme.palette.error.main, 0.1),
                color: theme.palette.error.main,
                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                '&:hover': {
                  bgcolor: alpha(theme.palette.error.main, 0.15),
                  transform: 'scale(1.05)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.2)}`
                },
                transition: 'all 0.2s ease'
              }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: 'auto' }}>
        {carregando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={48} />
          </Box>
        ) : erro ? (
          <Box sx={{ p: 3 }}>
            <Alert 
              severity="error" 
              action={
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={carregarDadosUsuario}
                  startIcon={<RefreshIcon />}
                >
                  Tentar Novamente
                </Button>
              }
            >
              {erro}
            </Alert>
          </Box>
        ) : (
          <>
            {/* Informações do Usuário - Design Moderno */}
            {dadosUsuario && (
              <Box 
                sx={{ 
                  position: 'relative',
                  background: `linear-gradient(135deg, ${alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.08)} 0%, ${alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.02)} 100%)`,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: `linear-gradient(90deg, ${customTheme?.palette?.primary?.main || theme.palette.primary.main}, ${alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.6)})`,
                    borderRadius: '0 0 8px 8px'
                  }
                }}
              >
                {/* Header do Perfil */}
                <Box sx={{ p: 4, pb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ position: 'relative', mr: 3 }}>
                      <Avatar
                        sx={{
                          width: 80,
                          height: 80,
                          bgcolor: customTheme?.palette?.primary?.main || theme.palette.primary.main,
                          color: 'white',
                          fontSize: '2rem',
                          fontWeight: 'bold',
                          boxShadow: `0 8px 24px ${alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.3)}`,
                          border: `4px solid ${alpha('#fff', 0.9)}`,
                          position: 'relative',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: -2,
                            left: -2,
                            right: -2,
                            bottom: -2,
                            borderRadius: '50%',
                            background: `linear-gradient(45deg, ${customTheme?.palette?.primary?.main || theme.palette.primary.main}, ${alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.7)})`,
                            zIndex: -1,
                            opacity: 0.3
                          }
                        }}
                      >
                        {dadosUsuario.nome.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          width: 24,
                          height: 24,
                          bgcolor: '#4caf50',
                          borderRadius: '50%',
                          border: `3px solid ${theme.palette.background.paper}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <CheckCircleIcon sx={{ fontSize: 12, color: 'white' }} />
                      </Box>
                    </Box>
                    
                    <Box sx={{ flex: 1 }}>
                      <Typography 
                        variant="h4" 
                        fontWeight="bold" 
                        sx={{ 
                          mb: 0.5,
                          background: `linear-gradient(45deg, ${customTheme?.palette?.primary?.main || theme.palette.primary.main}, ${alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.7)})`,
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          textShadow: `0 2px 4px ${alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.1)}`
                        }}
                      >
                        {dadosUsuario.nome}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Chip
                          icon={<PersonIcon sx={{ fontSize: 16 }} />}
                          label="Atendente"
                          size="small"
                          sx={{
                            bgcolor: alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.1),
                            color: customTheme?.palette?.primary?.main || theme.palette.primary.main,
                            fontWeight: 600,
                            border: `1px solid ${alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.2)}`
                          }}
                        />
                        <Chip
                          icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                          label="Ativo"
                          size="small"
                          sx={{
                            bgcolor: alpha('#4caf50', 0.1),
                            color: '#4caf50',
                            fontWeight: 600,
                            border: `1px solid ${alpha('#4caf50', 0.2)}`
                          }}
                        />
                      </Box>
                                             <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                         Relatório detalhado de atendimentos no período selecionado
                       </Typography>
                     </Box>
                     
                                          {/* Indicadores de Ranking Modernos */}
                     <Box sx={{ ml: 'auto', display: 'flex', gap: 4 }}>
                       {/* Ranking de Eficiência (Tempo Médio) */}
                       <Box sx={{ textAlign: 'center' }}>
                         <Box
                           sx={{
                             width: 90,
                             height: 90,
                             borderRadius: '24px',
                             background: rankingEficiencia === 1 
                               ? `linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF8C00 100%)` 
                               : rankingEficiencia <= 3 
                               ? `linear-gradient(135deg, #4CAF50 0%, #45a049 50%, #388e3c 100%)` 
                               : `linear-gradient(135deg, #FF9800 0%, #F57C00 50%, #E65100 100%)`,
                             display: 'flex',
                             flexDirection: 'column',
                             alignItems: 'center',
                             justifyContent: 'center',
                             boxShadow: rankingEficiencia === 1 
                               ? `0 12px 40px ${alpha('#FFD700', 0.4)}, 0 6px 20px ${alpha('#FFA500', 0.3)}` 
                               : rankingEficiencia <= 3 
                               ? `0 12px 40px ${alpha('#4CAF50', 0.4)}, 0 6px 20px ${alpha('#45a049', 0.3)}` 
                               : `0 12px 40px ${alpha('#FF9800', 0.4)}, 0 6px 20px ${alpha('#F57C00', 0.3)}`,
                             border: `2px solid ${alpha('#fff', 0.3)}`,
                             position: 'relative',
                             transform: 'perspective(1000px) rotateX(5deg)',
                             transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                             cursor: 'pointer',
                             '&:hover': {
                               transform: 'perspective(1000px) rotateX(0deg) translateY(-6px)',
                               boxShadow: rankingEficiencia === 1 
                                 ? `0 16px 50px ${alpha('#FFD700', 0.5)}, 0 8px 25px ${alpha('#FFA500', 0.4)}` 
                                 : rankingEficiencia <= 3 
                                 ? `0 16px 50px ${alpha('#4CAF50', 0.5)}, 0 8px 25px ${alpha('#45a049', 0.4)}` 
                                 : `0 16px 50px ${alpha('#FF9800', 0.5)}, 0 8px 25px ${alpha('#F57C00', 0.4)}`,
                             },
                             '&::before': {
                               content: '""',
                               position: 'absolute',
                               top: 0,
                               left: 0,
                               right: 0,
                               bottom: 0,
                               background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 50%, rgba(0,0,0,0.1) 100%)',
                               borderRadius: '24px',
                               zIndex: 1,
                             },
                             '&::after': rankingEficiencia === 1 ? {
                               content: '"👑"',
                               position: 'absolute',
                               top: -12,
                               right: -12,
                               fontSize: '24px',
                               zIndex: 3,
                               filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.4))',
                               animation: 'bounce 2s infinite',
                               '@keyframes bounce': {
                                 '0%, 20%, 50%, 80%, 100%': {
                                   transform: 'translateY(0)',
                                 },
                                 '40%': {
                                   transform: 'translateY(-8px)',
                                 },
                                 '60%': {
                                   transform: 'translateY(-4px)',
                                 },
                               }
                             } : {}
                           }}
                         >
                           <Box
                             sx={{
                               display: 'flex',
                               alignItems: 'center',
                               justifyContent: 'center',
                               width: 36,
                               height: 36,
                               borderRadius: '50%',
                               background: 'rgba(255,255,255,0.25)',
                               mb: 0.5,
                               zIndex: 2,
                               position: 'relative',
                               backdropFilter: 'blur(10px)',
                               border: '1px solid rgba(255,255,255,0.2)',
                             }}
                           >
                             <AccessTimeIcon sx={{ fontSize: 18, color: 'white' }} />
                           </Box>
                           <Typography 
                             variant="h5" 
                             sx={{ 
                               fontSize: '1.4rem', 
                               lineHeight: 1, 
                               zIndex: 2, 
                               position: 'relative',
                               color: 'white',
                               textShadow: '0 2px 8px rgba(0,0,0,0.4)',
                               fontWeight: 800,
                               letterSpacing: '-0.5px'
                             }}
                           >
                             {carregando ? '...' : `#${rankingEficiencia || '--'}`}
                           </Typography>
                         </Box>
                         <Typography 
                           variant="caption" 
                           sx={{ 
                             mt: 1.5, 
                             display: 'block', 
                             fontWeight: 700,
                             fontSize: '0.8rem',
                             color: 'text.primary',
                             letterSpacing: '0.5px',
                             textTransform: 'uppercase'
                           }}
                         >
                           Eficiência
                         </Typography>
                         <Typography 
                           variant="caption" 
                           sx={{ 
                             fontSize: '0.7rem',
                             color: 'text.secondary',
                             fontWeight: 500
                           }}
                         >
                           Tempo médio
                         </Typography>
                       </Box>

                       {/* Ranking de Volume (Quantidade) */}
                       <Box sx={{ textAlign: 'center' }}>
                         <Box
                           sx={{
                             width: 90,
                             height: 90,
                             borderRadius: '24px',
                             background: rankingVolume === 1 
                               ? `linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF8C00 100%)` 
                               : rankingVolume <= 3 
                               ? `linear-gradient(135deg, #4CAF50 0%, #45a049 50%, #388e3c 100%)` 
                               : `linear-gradient(135deg, #2196F3 0%, #1976D2 50%, #1565C0 100%)`,
                             display: 'flex',
                             flexDirection: 'column',
                             alignItems: 'center',
                             justifyContent: 'center',
                             boxShadow: rankingVolume === 1 
                               ? `0 12px 40px ${alpha('#FFD700', 0.4)}, 0 6px 20px ${alpha('#FFA500', 0.3)}` 
                               : rankingVolume <= 3 
                               ? `0 12px 40px ${alpha('#4CAF50', 0.4)}, 0 6px 20px ${alpha('#45a049', 0.3)}` 
                               : `0 12px 40px ${alpha('#2196F3', 0.4)}, 0 6px 20px ${alpha('#1976D2', 0.3)}`,
                             border: `2px solid ${alpha('#fff', 0.3)}`,
                             position: 'relative',
                             transform: 'perspective(1000px) rotateX(5deg)',
                             transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                             cursor: 'pointer',
                             '&:hover': {
                               transform: 'perspective(1000px) rotateX(0deg) translateY(-6px)',
                               boxShadow: rankingVolume === 1 
                                 ? `0 16px 50px ${alpha('#FFD700', 0.5)}, 0 8px 25px ${alpha('#FFA500', 0.4)}` 
                                 : rankingVolume <= 3 
                                 ? `0 16px 50px ${alpha('#4CAF50', 0.5)}, 0 8px 25px ${alpha('#45a049', 0.4)}` 
                                 : `0 16px 50px ${alpha('#2196F3', 0.5)}, 0 8px 25px ${alpha('#1976D2', 0.4)}`,
                             },
                             '&::before': {
                               content: '""',
                               position: 'absolute',
                               top: 0,
                               left: 0,
                               right: 0,
                               bottom: 0,
                               background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 50%, rgba(0,0,0,0.1) 100%)',
                               borderRadius: '24px',
                               zIndex: 1,
                             },
                             '&::after': rankingVolume === 1 ? {
                               content: '"👑"',
                               position: 'absolute',
                               top: -12,
                               right: -12,
                               fontSize: '24px',
                               zIndex: 3,
                               filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.4))',
                               animation: 'bounce 2s infinite',
                               '@keyframes bounce': {
                                 '0%, 20%, 50%, 80%, 100%': {
                                   transform: 'translateY(0)',
                                 },
                                 '40%': {
                                   transform: 'translateY(-8px)',
                                 },
                                 '60%': {
                                   transform: 'translateY(-4px)',
                                 },
                               }
                             } : {}
                           }}
                         >
                           <Box
                             sx={{
                               display: 'flex',
                               alignItems: 'center',
                               justifyContent: 'center',
                               width: 36,
                               height: 36,
                               borderRadius: '50%',
                               background: 'rgba(255,255,255,0.25)',
                               mb: 0.5,
                               zIndex: 2,
                               position: 'relative',
                               backdropFilter: 'blur(10px)',
                               border: '1px solid rgba(255,255,255,0.2)',
                             }}
                           >
                             <TrendingUpIcon sx={{ fontSize: 18, color: 'white' }} />
                           </Box>
                           <Typography 
                             variant="h5" 
                             sx={{ 
                               fontSize: '1.4rem', 
                               lineHeight: 1, 
                               zIndex: 2, 
                               position: 'relative',
                               color: 'white',
                               textShadow: '0 2px 8px rgba(0,0,0,0.4)',
                               fontWeight: 800,
                               letterSpacing: '-0.5px'
                             }}
                           >
                             {carregando ? '...' : `#${rankingVolume || '--'}`}
                           </Typography>
                         </Box>
                         <Typography 
                           variant="caption" 
                           sx={{ 
                             mt: 1.5, 
                             display: 'block', 
                             fontWeight: 700,
                             fontSize: '0.8rem',
                             color: 'text.primary',
                             letterSpacing: '0.5px',
                             textTransform: 'uppercase'
                           }}
                         >
                           Volume
                         </Typography>
                         <Typography 
                           variant="caption" 
                           sx={{ 
                             fontSize: '0.7rem',
                             color: 'text.secondary',
                             fontWeight: 500
                           }}
                         >
                           Atendimentos
                         </Typography>
                       </Box>
                     </Box>
                   </Box>
                 </Box>

                {/* Cards de Métricas Modernos */}
                <Box sx={{ px: 4, pb: 4 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Card 
                        elevation={0} 
                        sx={{ 
                          background: `linear-gradient(135deg, ${alpha('#2196f3', 0.1)} 0%, ${alpha('#2196f3', 0.05)} 100%)`,
                          border: `1px solid ${alpha('#2196f3', 0.15)}`,
                          borderRadius: 3,
                          position: 'relative',
                          overflow: 'hidden',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '4px',
                            height: '100%',
                            background: 'linear-gradient(180deg, #2196f3, #1976d2)'
                          },
                                                     '&:hover': {
                             transform: 'translateY(-2px)',
                             transition: 'all 0.3s ease',
                             boxShadow: `0 8px 25px ${alpha('#2196f3', 0.15)}`
                           },
                           animation: 'slideInLeft 0.6s ease-out',
                           '@keyframes slideInLeft': {
                             '0%': {
                               opacity: 0,
                               transform: 'translateX(-30px)'
                             },
                             '100%': {
                               opacity: 1,
                               transform: 'translateX(0)'
                             }
                           }
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                              <Typography variant="h3" fontWeight="bold" sx={{ color: '#2196f3', mb: 0.5 }}>
                                {dadosUsuario.totalAtendimentos}
                              </Typography>
                              <Typography variant="body1" fontWeight={600} color="text.primary">
                                Total de Atendimentos
                              </Typography>
                                                             <Typography variant="caption" color="text.secondary">
                                 Senhas processadas no período
                               </Typography>
                             </Box>
                            <Box
                              sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                background: `linear-gradient(135deg, #2196f3, #1976d2)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: `0 8px 20px ${alpha('#2196f3', 0.3)}`
                              }}
                            >
                              <AssignmentIcon sx={{ fontSize: 28, color: 'white' }} />
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Card 
                        elevation={0} 
                        sx={{ 
                          background: `linear-gradient(135deg, ${alpha('#ff9800', 0.1)} 0%, ${alpha('#ff9800', 0.05)} 100%)`,
                          border: `1px solid ${alpha('#ff9800', 0.15)}`,
                          borderRadius: 3,
                          position: 'relative',
                          overflow: 'hidden',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '4px',
                            height: '100%',
                            background: 'linear-gradient(180deg, #ff9800, #f57c00)'
                          },
                                                     '&:hover': {
                             transform: 'translateY(-2px)',
                             transition: 'all 0.3s ease',
                             boxShadow: `0 8px 25px ${alpha('#ff9800', 0.15)}`
                           },
                           animation: 'slideInRight 0.6s ease-out 0.2s both',
                           '@keyframes slideInRight': {
                             '0%': {
                               opacity: 0,
                               transform: 'translateX(30px)'
                             },
                             '100%': {
                               opacity: 1,
                               transform: 'translateX(0)'
                             }
                           }
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                              <Typography variant="h3" fontWeight="bold" sx={{ color: '#ff9800', mb: 0.5 }}>
                                {dadosUsuario.tempoMedioAtendimento}
                              </Typography>
                              <Typography variant="body1" fontWeight={600} color="text.primary">
                                Tempo Médio
                              </Typography>
                                                             <Typography variant="caption" color="text.secondary">
                                 Por atendimento realizado
                               </Typography>
                             </Box>
                            <Box
                              sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                background: `linear-gradient(135deg, #ff9800, #f57c00)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: `0 8px 20px ${alpha('#ff9800', 0.3)}`
                              }}
                            >
                              <AccessTimeIcon sx={{ fontSize: 28, color: 'white' }} />
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    {/* Novo Card de Serviços Atendidos */}
                    <Grid item xs={12}>
                      <Card 
                        elevation={0} 
                        sx={{ 
                          background: `linear-gradient(135deg, ${alpha('#9c27b0', 0.1)} 0%, ${alpha('#9c27b0', 0.05)} 100%)`,
                          border: `1px solid ${alpha('#9c27b0', 0.15)}`,
                          borderRadius: 3,
                          position: 'relative',
                          overflow: 'hidden',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '4px',
                            height: '100%',
                            background: 'linear-gradient(180deg, #9c27b0, #7b1fa2)'
                          },
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            transition: 'all 0.3s ease',
                            boxShadow: `0 8px 25px ${alpha('#9c27b0', 0.15)}`
                          },
                          animation: 'slideInUp 0.6s ease-out 0.4s both',
                          '@keyframes slideInUp': {
                            '0%': {
                              opacity: 0,
                              transform: 'translateY(30px)'
                            },
                            '100%': {
                              opacity: 1,
                              transform: 'translateY(0)'
                            }
                          }
                        }}
                      >
                        <CardContent sx={{ p: 2.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Box>
                              <Typography variant="h6" fontWeight="bold" sx={{ color: '#9c27b0', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.1rem' }}>
                                <CategoryIcon sx={{ fontSize: 20 }} />
                                Serviços Atendidos
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                {dadosUsuario.totalServicosDistintos || 0} tipos diferentes no período
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontStyle: 'italic' }}>
                                * Inclui serviços adicionais atendidos em conjunto
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                background: `linear-gradient(135deg, #9c27b0, #7b1fa2)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: `0 6px 16px ${alpha('#9c27b0', 0.3)}`
                              }}
                            >
                              <Typography variant="h6" fontWeight="bold" sx={{ color: 'white', fontSize: '1rem' }}>
                                {dadosUsuario.totalServicosDistintos || 0}
                              </Typography>
                            </Box>
                          </Box>
                          
                          {dadosUsuario.servicosAtendidos && dadosUsuario.servicosAtendidos.length > 0 ? (
                            <Grid container spacing={1}>
                              {dadosUsuario.servicosAtendidos.map((servico, index) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={servico.nome}>
                                  <Box
                                    sx={{
                                      p: 1.2,
                                      borderRadius: 1.5,
                                      background: `linear-gradient(135deg, ${alpha('#9c27b0', 0.06)} 0%, ${alpha('#9c27b0', 0.02)} 100%)`,
                                      border: `1px solid ${alpha('#9c27b0', 0.12)}`,
                                      position: 'relative',
                                      overflow: 'hidden',
                                      transition: 'all 0.2s ease',
                                      height: 'fit-content',
                                      minHeight: 65,
                                      '&:hover': {
                                        transform: 'translateY(-1px)',
                                        boxShadow: `0 4px 12px ${alpha('#9c27b0', 0.15)}`,
                                        borderColor: alpha('#9c27b0', 0.25)
                                      },
                                      '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '3px',
                                        height: '100%',
                                        background: '#9c27b0'
                                      }
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.3 }}>
                                      <Typography variant="body2" fontWeight={600} sx={{ 
                                        color: 'text.primary',
                                        fontSize: '0.75rem',
                                        lineHeight: 1.2,
                                        flex: 1,
                                        mr: 0.8,
                                        overflow: 'hidden',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        textOverflow: 'ellipsis'
                                      }}>
                                        {servico.nome}
                                      </Typography>
                                      <Chip
                                        label={servico.quantidade}
                                        size="small"
                                        sx={{
                                          height: 18,
                                          fontSize: '0.65rem',
                                          fontWeight: 'bold',
                                          bgcolor: '#9c27b0',
                                          color: 'white',
                                          minWidth: 28,
                                          '& .MuiChip-label': {
                                            px: 0.8
                                          }
                                        }}
                                      />
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', opacity: 0.8 }}>
                                      {((servico.quantidade / dadosUsuario.totalAtendimentos) * 100).toFixed(1)}%
                                    </Typography>
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          ) : (
                            <Alert severity="info" sx={{ mt: 2 }}>
                              Nenhum serviço encontrado para este período.
                            </Alert>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            )}

            <Divider />

            {/* Tabela de Senhas */}
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssignmentIcon />
                Senhas Atendidas ({senhas.length})
              </Typography>
              
              {senhas.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Nenhuma senha encontrada para este usuário no período selecionado.
                </Alert>
              ) : (
                <>
                  <TableContainer 
                    component={Paper} 
                    elevation={0} 
                    sx={{ 
                      border: `1px solid ${alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.1)}`, 
                      borderRadius: 2, 
                      mt: 2,
                      overflowX: 'auto',
                      '&::-webkit-scrollbar': {
                        height: 8,
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.1),
                        borderRadius: 4,
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.3),
                        borderRadius: 4,
                        '&:hover': {
                          backgroundColor: alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.5),
                        }
                      }
                    }}
                  >
                    <Table sx={{ minWidth: 1600 }}>
                      <TableHead>
                        <TableRow sx={{ bgcolor: alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.05) }}>
                          <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Senha</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Serviço</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 100 }} align="center">Tipo</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 120 }} align="center">Status</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 80 }} align="center">Guichê</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 140 }} align="center">Emissão</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 140 }} align="center">Chamada</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 140 }} align="center">Início</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 140 }} align="center">Finalização</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 140 }} align="center">Tempo Atendimento</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 180 }} align="center">Serviços Adicionais</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {senhasPaginadas.map((senha, index) => (
                          <TableRow
                            key={`${senha.id}-${senha.origem}`}
                            hover
                            sx={{
                              '&:hover': {
                                bgcolor: alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.04),
                              }
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body1" fontWeight="bold">
                                  {senha.senha}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="body2">
                                  {senha.servico_nome}
                                </Typography>
                                {senha.servico_alterado && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                    <SwapHorizIcon sx={{ fontSize: 12, color: theme.palette.warning.main }} />
                                    <Typography variant="caption" color="warning.main">
                                      Alterado de: {senha.servico_original}
                                    </Typography>
                                  </Box>
                                )}
                                {senha.servicos_adicionais && senha.servicos_adicionais.length > 0 && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                    <AddIcon sx={{ fontSize: 12, color: theme.palette.info.main }} />
                                    <Typography variant="caption" color="info.main">
                                      +{senha.servicos_adicionais.length} adicional(is)
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={senha.preferencial ? 'Preferencial' : 'Comum'}
                                size="small"
                                sx={{
                                  bgcolor: senha.preferencial 
                                    ? alpha('#f44336', 0.1) 
                                    : alpha('#2196f3', 0.1),
                                  color: senha.preferencial 
                                    ? '#f44336' 
                                    : '#2196f3',
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  border: `1px solid ${senha.preferencial 
                                    ? alpha('#f44336', 0.3) 
                                    : alpha('#2196f3', 0.3)
                                  }`,
                                  minWidth: '80px'
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                icon={getStatusIcon(senha.status)}
                                label={traduzirStatus(senha.status)}
                                size="small"
                                sx={{
                                  bgcolor: alpha(getStatusColor(senha.status), 0.1),
                                  color: getStatusColor(senha.status),
                                  fontWeight: 600
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                <RoomIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                                <Typography variant="body2">
                                  {senha.guiche || '--'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                <CalendarIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                                <Typography variant="caption">
                                  {formatarData(senha.emitida_em)}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="caption">
                                {formatarData(senha.chamada_em)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="caption">
                                {formatarData(senha.iniciada_em)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="caption">
                                {formatarData(senha.finalizada_em)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                <AccessTimeIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                                <Chip
                                  label={calcularTempoAtendimento(senha)}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    bgcolor: calcularTempoAtendimento(senha) !== '--' 
                                      ? alpha(theme.palette.success.main, 0.1) 
                                      : alpha(theme.palette.grey[500], 0.1),
                                    color: calcularTempoAtendimento(senha) !== '--' 
                                      ? theme.palette.success.main 
                                      : theme.palette.text.secondary,
                                    borderColor: calcularTempoAtendimento(senha) !== '--' 
                                      ? alpha(theme.palette.success.main, 0.3) 
                                      : alpha(theme.palette.grey[500], 0.3),
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    minWidth: '80px',
                                    fontFamily: 'monospace'
                                  }}
                                />
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Box>
                                {senha.servicos_adicionais && senha.servicos_adicionais.length > 0 ? (
                                  <Box>
                                    {senha.servicos_adicionais.map((servico, idx) => (
                                      <Chip
                                        key={idx}
                                        label={servico}
                                        size="small"
                                        sx={{
                                          m: 0.25,
                                          bgcolor: alpha(theme.palette.info.main, 0.1),
                                          color: theme.palette.info.main,
                                          fontSize: '0.7rem',
                                          height: 20,
                                          '& .MuiChip-label': {
                                            px: 0.5
                                          }
                                        }}
                                      />
                                    ))}
                                  </Box>
                                ) : (
                                  <Typography variant="caption" color="text.secondary">
                                    --
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  <TablePagination
                    component="div"
                    count={senhas.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage="Senhas por página:"
                    labelDisplayedRows={({ from, to, count }) => 
                      `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
                    }
                    sx={{
                      borderTop: `1px solid ${alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.1)}`,
                      bgcolor: alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.02)
                    }}
                  />
                </>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          borderTop: `1px solid ${alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.1)}`,
          bgcolor: alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.02),
        }}
      >
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            onClick={carregarDadosUsuario}
            startIcon={<RefreshIcon />}
            disabled={carregando}
            variant="outlined"
            sx={{
              color: customTheme?.palette?.primary?.main || theme.palette.primary.main,
              borderColor: alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.3),
              '&:hover': {
                borderColor: customTheme?.palette?.primary?.main || theme.palette.primary.main,
                bgcolor: alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.05),
              }
            }}
          >
            Atualizar
          </Button>
          
          <Button
            onClick={handleImprimir}
            startIcon={<PrintIcon />}
            variant="outlined"
            disabled={!dadosUsuario || senhas.length === 0 || carregando}
            sx={{
              color: '#4caf50',
              borderColor: alpha('#4caf50', 0.3),
              '&:hover': {
                borderColor: '#4caf50',
                bgcolor: alpha('#4caf50', 0.05),
              },
              '&:disabled': {
                color: theme.palette.action.disabled,
                borderColor: alpha(theme.palette.action.disabled, 0.3),
              }
            }}
          >
            Imprimir Relatório
          </Button>
        </Box>
        
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            bgcolor: customTheme?.palette?.primary?.main || theme.palette.primary.main,
            '&:hover': {
              bgcolor: alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.8),
            }
          }}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalSenhasUsuario; 